import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Import images
import salesAgent1 from '../assets/Screenshot from 2025-06-07 23-26-28.png';
import salesAgent2 from '../assets/Screenshot from 2025-06-07 23-51-23.png';
import graphrag1 from '../assets/Pasted image.png';
import graphrag2 from '../assets/Screenshot from 2025-06-09 22-45-42.png';
import graphrag3 from '../assets/Screenshot from 2025-06-10 03-26-41.png';

// Import synthetic survey images
import syntheticSurveyCover from '../assets/66672a8715a56de4cc0238cf_Conducting Market Research for Your Online Business (2024 Guide).png';
import productConcept1 from '../assets/Screenshot from 2025-06-11 12-13-57.png';
import productConcept2 from '../assets/Screenshot from 2025-06-11 12-15-37.png';
import packTesting1 from '../assets/Screenshot from 2025-06-11 12-16-37.png';
import packTesting2 from '../assets/Screenshot from 2025-06-11 12-17-25.png';
import pricingFeedback1 from '../assets/Screenshot from 2025-06-11 12-18-19.png';
import pricingFeedback2 from '../assets/Screenshot from 2025-06-11 12-19-53.png';

// Import Astra sales agent images
import astraSalesCover from '../assets/istockphoto-1465545513-612x612.jpg';
import astraChatbotInterface from '../assets/1.png';
import astraChatbotResponse from '../assets/2.png';

// Import TextGrad cover image
import textgradCover from '../assets/Screenshot from 2025-06-11 13-37-05.png';

// Import GraphRAG images
import graphragCover from '../assets/social-network-blue-art-gg6rzoh6wxyo7xqr.jpg';
import conceptStructureGraph from '../assets/Screenshot from 2025-06-10 03-26-41.png';
import multimodalGraph from '../assets/Screenshot from 2025-06-09 22-45-42.png';

const DetailSection = styled.section`
  padding-top: 8rem;
  background-color: #121212;
  min-height: 100vh;

  @media (max-width: 480px) {
    padding-top: 6rem;
  }
`;

const BackButton = styled.button`
  background-color: transparent;
  color: #3b82f6;
  border: 2px solid #3b82f6;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  font-weight: 500;
  margin-bottom: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #3b82f6;
    color: white;
  }
`;

const HeroImage = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
  border-radius: 15px;
  margin-bottom: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 250px;
  }

  @media (max-width: 480px) {
    height: 180px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .icon {
    font-size: 6rem;
    color: white;
    opacity: 0.8;
  }
`;

const ContentContainer = styled.div`
  margin-top: 2rem;
  width: 100%;
`;

const MainContent = styled.div`
  .content-text {
    color: #e0e0e0;
    line-height: 1.8;
    font-size: 1.1rem;
    margin-bottom: 2rem;

    @media (max-width: 480px) {
      font-size: 1rem;
    }

    h1 {
      color: #3b82f6;
      font-size: 2.5rem;
      font-weight: 700;
      margin: 2rem 0 1rem 0;
      line-height: 1.2;

      @media (max-width: 480px) {
        font-size: 1.6rem;
      }
    }

    h2 {
      color: #3b82f6;
      font-size: 2rem;
      font-weight: 600;
      margin: 2rem 0 1rem 0;
      line-height: 1.3;

      @media (max-width: 480px) {
        font-size: 1.4rem;
      }
    }

    h3 {
      color: #e0e0e0;
      font-size: 1.5rem;
      font-weight: 600;
      margin: 1.5rem 0 1rem 0;
      line-height: 1.4;

      @media (max-width: 480px) {
        font-size: 1.2rem;
      }
    }

    h4 {
      color: #b0b0b0;
      font-size: 1.2rem;
      font-weight: 500;
      margin: 1rem 0 0.5rem 0;
      line-height: 1.4;
    }

    p {
      margin-bottom: 1.5rem;
      color: #e0e0e0;
    }

    ul {
      margin: 1rem 0;
      padding-left: 0;
      list-style: none;

      li {
        margin-bottom: 0.5rem;
        padding-left: 1.5rem;
        position: relative;
        color: #e0e0e0;

        &:before {
          content: '•';
          color: #3b82f6;
          font-weight: bold;
          position: absolute;
          left: 0;
        }
      }
    }

    .quote {
      background-color: #2a2a2a;
      border-left: 4px solid #3b82f6;
      padding: 1rem 1.5rem;
      margin: 1.5rem 0;
      font-style: italic;
      color: #b0b0b0;
    }

    .comparison-table {
      margin: 2rem 0;
      overflow-x: auto;

      table {
        width: 100%;
        border-collapse: collapse;
        background-color: #1e1e1e;
        border-radius: 10px;
        overflow: hidden;

        th {
          background-color: #3b82f6;
          color: white;
          padding: 1rem;
          text-align: center;
          font-weight: 600;
        }

        td {
          padding: 1rem;
          text-align: center;
          border-bottom: 1px solid #333;
          color: #e0e0e0;
        }

        tr:last-child td {
          border-bottom: none;
        }

        tr:nth-child(even) {
          background-color: #2a2a2a;
        }
      }
    }

    .section-divider {
      border: none;
      height: 2px;
      background: linear-gradient(90deg, transparent, #3b82f6, transparent);
      margin: 2rem 0;
    }

    .section-image {
      width: 80%;
      margin: 2rem auto;
      display: block;
      border-radius: 10px;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

      @media (max-width: 480px) {
        width: 100%;
      }
    }

    .section-images {
      margin: 2rem 0;
      text-align: center;
    }
  }

  .images-gallery {
    margin: 2rem 0;
  }

  .gallery-image {
    width: 80%;
    margin: 1rem auto;
    display: block;
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

    @media (max-width: 480px) {
      width: 100%;
    }
  }
`;


const CategoryBadge = styled.span`
  background-color: #3b82f6;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-right: 0.5rem;
`;

const getContentWithImages = (study) => {
  if (study.slug === 'synthetic-survey-suite') {
    let content = study.content;
    
    // Replace product concept testing images
    content = content.replace(
      /<div class="section-images">\s*<img src="\$\{productConcept1\}"[^>]*>\s*<img src="\$\{productConcept2\}"[^>]*>\s*<\/div>/g,
      `<div class="section-images">\n<img src="${productConcept1}" alt="Product Concept Testing Example 1" class="section-image" />\n<img src="${productConcept2}" alt="Product Concept Testing Example 2" class="section-image" />\n</div>`
    );
    
    // Replace pack testing images
    content = content.replace(
      /<div class="section-images">\s*<img src="\$\{packTesting1\}"[^>]*>\s*<img src="\$\{packTesting2\}"[^>]*>\s*<\/div>/g,
      `<div class="section-images">\n<img src="${packTesting1}" alt="Pack Testing Example 1" class="section-image" />\n<img src="${packTesting2}" alt="Pack Testing Example 2" class="section-image" />\n</div>`
    );
    
    // Replace pricing feedback images
    content = content.replace(
      /<div class="section-images">\s*<img src="\$\{pricingFeedback1\}"[^>]*>\s*<img src="\$\{pricingFeedback2\}"[^>]*>\s*<\/div>/g,
      `<div class="section-images">\n<img src="${pricingFeedback1}" alt="Pricing Feedback Example 1" class="section-image" />\n<img src="${pricingFeedback2}" alt="Pricing Feedback Example 2" class="section-image" />\n</div>`
    );
    
    return content;
  }
  
  if (study.slug === 'astra-sales-chatbot') {
    let content = study.content;
    
    // Replace Astra chatbot images
    content = content.replace(
      /ASTRA_INTERFACE_PLACEHOLDER/g,
      astraChatbotInterface
    );
    
    content = content.replace(
      /ASTRA_RESPONSE_PLACEHOLDER/g,
      astraChatbotResponse
    );
    
    return content;
  }
  
  if (study.slug === 'nexo-graphrag-engine') {
    let content = study.content;
    
    // Replace GraphRAG images
    content = content.replace(
      /CONCEPT_STRUCTURE_PLACEHOLDER/g,
      conceptStructureGraph
    );
    
    content = content.replace(
      /MULTIMODAL_GRAPH_PLACEHOLDER/g,
      multimodalGraph
    );
    
    return content;
  }
  
  return study.content;
};

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Case studies data (same as in CaseStudies.js)
  const caseStudies = [
    {
      categories: ['E-commerce', 'Media and Entertainment'],
      title: 'Astra: Custom Tailored Agentic AI Chatbots',
      description: 'Reimagining online sales with personalized AI sales assistants that boost conversions, save costs and reduce human effort through intelligent customer engagement.',
      icon: '🤖',
      image: astraSalesCover,
      images: [salesAgent1, salesAgent2],
      metrics: [
        { value: '24/7', label: 'Availability' },
        { value: '95%', label: 'Query Resolution' },
        { value: '40%', label: 'Conversion Boost' }
      ],
      technologies: ['LangGraph', 'React/Django Frontend', 'GraphRAG', 'Multi-channel Deployment'],
      slug: 'astra-sales-chatbot',
      content: `<h2>Custom Tailored Agentic AI Chatbots: Reimagine Online Sales, Boost Conversions, Save Costs and Human Efforts</h2>

<p><strong>How ContextJet AI Built a Personalized Sales Chatbot to Power E-Commerce Experiences</strong></p>

<hr class="section-divider">

<h3>Overview</h3>

<p>In the fast-moving world of e-commerce, engaging customers at the right time, with the right information, is critical. But not every online store has the resources to offer real-time, knowledgeable, 24/7 sales support. That's exactly where <strong>Astra</strong>, our intelligent AI sales assistant, comes in.</p>

<p>This chatbot was originally developed for a client under a different brand. For demonstration and confidentiality purposes, we've used hypothetical names like <strong>Zofer Electronics</strong> (as the e-commerce business) and <strong>Astra</strong> (as the chatbot). The underlying product and catalog data showcased here have been <strong>synthesized using our Synthetic Data Generation Suite</strong> to mimic a realistic retail environment while maintaining complete privacy.</p>

<p>While the names are fictional, <strong>Astra is very real</strong> - a flexible, agentic sales chatbot that combines powerful AI models, real-time data sources, and smart persona logic to deliver a conversational experience that feels both <strong>personal and informed</strong>.</p>

<hr class="section-divider">

<h3>The Challenge</h3>

<p>E-commerce platforms today face a number of real-world challenges:</p>

<ul>
<li>Visitors drop off without buying because no one is there to guide them.</li>
<li>Sales reps can't be online 24/7.</li>
<li>Product catalogs are complex, often leaving users overwhelmed.</li>
<li>Customers want real comparisons, real-time trends, and personalized suggestions.</li>
</ul>

<p>Our goal was to build a <strong>conversational AI sales agent</strong> that could solve all of this - without needing a large customer support team behind the scenes.</p>

<hr class="section-divider">

<h3>The Solution: Astra, The Agentic AI Sales Assistant</h3>

<p>Astra was designed to function like a <strong>well-trained product expert</strong> - capable of helping customers explore a store's offerings, compare products, retrieve reviews, and even suggest bundles or upsells.</p>

<p>Built using the highly flexible <strong>LangGraph</strong> framework (to suit our AI engineering strengths), Astra is a modular system that can be extended, customized, and integrated into almost any client environment.</p>

<p>Key highlights of the solution include:</p>

<ul>
<li>🤖 <strong>Agentic AI Architecture:</strong> Supports <strong>custom flows</strong>, autonomous sub-agent decisions, and layered thinking.</li>
<li>🔎 <strong>Tool Integrations:</strong> Astra can connect to:
<ul>
<li>Web search for live market insights</li>
<li>RAG and <strong>GraphRAG</strong> tools for structured knowledge retrieval</li>
<li>Client-specific <strong>databases or APIs</strong> for real-time inventory, pricing, or CRM data</li>
<li>Advanced API routing logic via <strong>MCP</strong>, <strong>LangGraph</strong>, or alternatives like <strong>n8n</strong>, <strong>CrewAI</strong>, and <strong>AutoGen</strong></li>
</ul>
</li>
<li>💬 <strong>Conversational Frontend:</strong> Astra supports frontends built in <strong>React</strong>, <strong>Django</strong>, <strong>Flask</strong>, and <strong>Streamlit</strong>, with flexibility for others depending on client needs.</li>
</ul>

<hr class="section-divider">

<h3>Behind the Scenes: What Makes Astra Tick</h3>

<p>Astra isn't a one-size-fits-all chatbot. It's a <strong>framework-powered engine</strong> that can be reshaped for any business logic.</p>

<p>In our Zofer Electronics demo, Astra could:</p>

<ul>
<li>Compare Zofer's products with competitors like GoPro</li>
<li>Pull real-time pricing and reviews from the internet</li>
<li>Guide users through multiple product categories (e.g., security cameras, robot vacuums, etc.)</li>
<li>Retrieve data from a product database instantly</li>
<li>Provide tailored upsells and suggestions depending on the user's interest</li>
</ul>

<p>You can see this in action in the screenshots:<br>
✅ A user asks how Zofer's action camera compares with GoPro<br>
✅ Astra begins <strong>searching the web</strong>, scanning the internal product DB, and composing a well-rounded answer<br>
✅ It returns a structured yet conversational sales pitch - highlighting specs, features, pricing, and value comparisons</p>

<div class="section-images">
<img src="ASTRA_INTERFACE_PLACEHOLDER" alt="Astra Chatbot Interface - Web Search in Action" class="section-image" />
<img src="ASTRA_RESPONSE_PLACEHOLDER" alt="Astra Chatbot Response - Detailed Product Comparison" class="section-image" />
</div>

<p>All of this happened <strong>within seconds</strong> - without a human sales rep needing to step in.</p>

<hr class="section-divider">

<h3>Smart Follow-Ups: Sales That Don't Stop at Hello</h3>

<p>One of Astra's most valuable features is its ability to <strong>proactively follow up with potential customers</strong> based on intelligent triggers.</p>

<p>For example, Astra can automatically engage users when:</p>

<ul>
<li>🛒 A cart has been abandoned</li>
<li>📩 A customer asked a product question but didn't return</li>
<li>🔔 There's a price drop or back-in-stock alert</li>
<li>💡 A product gets new reviews or updates</li>
<li>⏰ A user browsed a product multiple times without taking action</li>
<li>🏷️ A targeted promotion or seasonal campaign applies to their interests</li>
</ul>

<p>These follow-ups can be personalized, timed strategically, and delivered via chat, email, or even in-app nudges - maximizing the chance of converting interest into action.</p>

<hr class="section-divider">

<h3>Multi-Channel Deployment, Messaging Ready</h3>

<p>Beyond websites and in-app experiences, this chatbot can be deployed across <strong>popular communication platforms</strong> like <strong>WhatsApp</strong>, <strong>Facebook Messenger</strong>, <strong>Telegram</strong>, and <strong>Slack</strong> - ensuring seamless engagement no matter where customers are. Astra can also be adapted to channels like <strong>Shopify Inbox</strong> or <strong>custom webhook integrations</strong>, making it a great fit for businesses looking to meet customers where they already spend their time. This level of channel flexibility is especially valuable for global e-commerce brands and a top request across multiple clients.</p>

<hr class="section-divider">

<h3>Built for Customization</h3>

<p>Astra is not just a tool - it's a base solution designed for extension. For each client, we can:</p>

<ul>
<li>Integrate with internal tools like CRM, inventory databases, or sales analytics systems</li>
<li>Add personalized logic (e.g., loyalty discounts, VIP routing, seasonal campaigns)</li>
<li>Customize UI components or workflows to match brand tone and design</li>
<li>Deploy across platforms (web, mobile, or even in-store kiosks)</li>
</ul>

<p>And because Astra is <strong>agentic</strong>, it can <strong>autonomously switch tools, seek additional context, or loop in fallback logic</strong> when needed - making the conversation feel natural and helpful.</p>

<hr class="section-divider">

<h3>Business Value: Why Clients Love It</h3>

<p>Here's what Astra brings to the table for e-commerce brands:</p>

<div class="comparison-table">
<table>
<tr>
<th>Benefit</th>
<th>Traditional Chatbot</th>
<th>Astra AI Assistant</th>
</tr>
<tr>
<td>Product Search</td>
<td>Basic matching</td>
<td>Contextual, feature-rich</td>
</tr>
<tr>
<td>Competitor Comparison</td>
<td>Manual</td>
<td>Automated, web-sourced</td>
</tr>
<tr>
<td>Personalization</td>
<td>Scripted</td>
<td>Persona-driven logic</td>
</tr>
<tr>
<td>Tool Switching</td>
<td>None</td>
<td>Built-in tools & autonomy</td>
</tr>
<tr>
<td>Frontend Flexibility</td>
<td>Limited</td>
<td>React, Django, Flask, etc.</td>
</tr>
<tr>
<td>Custom Logic Integration</td>
<td>Minimal</td>
<td>Fully supported</td>
</tr>
<tr>
<td>Follow-Up Automation</td>
<td>Rare or manual</td>
<td>Trigger-based, dynamic</td>
</tr>
</table>
</div>

<p>With Astra, clients gain <strong>an always-on sales rep</strong> that learns, adapts, and operates at the speed of modern commerce.</p>

<hr class="section-divider">

<h3>Final Thoughts: Smart Conversations Sell Better</h3>

<p>At <strong>ContextJet AI</strong>, we've always aimed to build AI that actually solves problems - not just demos well. Astra is a perfect example of that vision. It bridges the gap between static e-commerce and <strong>dynamic, helpful conversations</strong> that drive confidence and conversions.</p>

<p>Whether you're a marketplace, D2C brand, or B2B seller, we can help you build your own Astra-like assistant - with your data, your tone, and your goals in mind.</p>

<p><strong>Let smart sales conversations be your unfair advantage.</strong></p>`
    },
    {
      categories: ['E-commerce', 'Manufacturing', 'Media and Entertainment'],
      title: 'Synthetic Market Survey Suite',
      description: 'Revolutionizing market research by generating quality insights in hours instead of weeks using AI-powered synthetic survey responses across multiple customer personas.',
      icon: '📊',
      image: syntheticSurveyCover,
      images: [],
      metrics: [
        { value: '3 hrs', label: 'Time to Insights' },
        { value: '10+', label: 'Segments Tested' },
        { value: '90%', label: 'Cost Reduction' }
      ],
      technologies: ['GPT-4o', 'LangChain Agents', 'Web Crawling', 'Custom Personas'],
      slug: 'synthetic-survey-suite',
      content: `<h2>Revolutionizing Market Research with ContextJet AI's Synthetic Market Survey Suite</h2>

<hr class="section-divider">

<h3>From Weeks to Hours: Rethinking Market Insights with AI</h3>

<p>At <strong>ContextJet AI</strong>, we've always believed that innovation shouldn't be slowed down by outdated research processes. For years, companies have relied on traditional surveys - ones that take weeks to design, run, and analyze - to figure out if their product, price, or packaging is on the right track. But let's be honest: that process can be slow, expensive, and inflexible.</p>

<p>That's exactly the problem one of our global consumer goods clients faced. Their team needed quick, reliable feedback on multiple product ideas across different types of customers.</p>

<p>Their question was simple:</p>

<div class="quote">"Can we get quality market feedback without having to run a full survey each time?"</div>

<p>Our answer? <strong>Absolutely. And faster than you think.</strong></p>

<hr class="section-divider">

<h3>The Problem: Old-School Surveys Slow You Down</h3>

<p>Traditional market research comes with some familiar roadblocks:</p>

<ul>
<li>⏱️ <strong>It's slow.</strong> Getting insights from a standard survey can take 2 to 4 weeks.</li>
<li>💸 <strong>It's pricey.</strong> You need to pay for survey design, participant recruitment, and quality checks.</li>
<li>🏷 <strong>It's narrow.</strong> You're often limited to a small number of target segments.</li>
<li>🤐 <strong>It's rigid.</strong> Trying out new ideas or pricing strategies usually means starting from scratch.</li>
</ul>

<p>Our client needed a smarter, more agile way to gather consumer feedback - something that could keep up with their fast-moving product teams.</p>

<hr class="section-divider">

<h3>The Solution: Our Synthetic Market Survey Suite</h3>

<p>We introduced them to our <strong>Synthetic Market Survey Suite</strong> - a next-generation research tool powered by AI. Instead of recruiting real people for every survey, our platform simulates detailed, human-like survey responses from <strong>persona-specific AI agents</strong>.</p>

<p>Here's how we make it work:</p>

<ul>
<li>🤖 <strong>Custom LLM pipelines</strong> built on GPT-4o with</li>
<li>🖼️ <strong>Text, Image as well as Tabular</strong> input data capabilities with custom fields and classes for convenience</li>
<li>🕷️ <strong>Web crawlers</strong> that gather real-world context and insights</li>
<li>🤖 <strong>Persona-based prompt design</strong> that mirrors actual consumer behavior</li>
<li>📝 <strong>LangChain-powered agents</strong> that bring it all together</li>
<li>🔗 <strong>Database connectivity</strong> to client-specific internal systems for richer context</li>
<li>🌐 <strong>Real-time web scraping</strong> to incorporate trending topics, live market data, and customer sentiment from the open web</li>
</ul>

<p>In short: it's like having a research panel that's always ready, endlessly scalable, and deeply tuned to your market.</p>

<hr class="section-divider">

<h3>What's Inside the Suite: Real Scenarios, Real Answers</h3>

<p>We built three main modules, each designed to solve a common product and marketing challenge:</p>

<h4>1. Product Concept Testing</h4>

<p>Want to know how your product idea will land with your audience? We simulate responses that assess:</p>

<ul>
<li>How well people understand the product</li>
<li>Whether they find it appealing or innovative</li>
<li>Their likelihood to buy - and why</li>
</ul>

<p>⏰ <strong>Turnaround Time:</strong> Under 3 hours for 5 product concepts across 6 personas.</p>

<div class="section-images">
<img src="${productConcept1}" alt="Product Concept Testing Example 1" class="section-image" />
<img src="${productConcept2}" alt="Product Concept Testing Example 2" class="section-image" />
</div>

<h4>2. Pack Testing (Packaging Perception)</h4>

<p>Design matters. We help brands test how their packaging affects:</p>

<ul>
<li>Shelf visibility</li>
<li>Aesthetic appeal</li>
<li>Trustworthiness</li>
<li>Memorability</li>
</ul>

<p>🎨 <strong>Result:</strong> Our client used these insights to confidently choose between two top designs - without printing a single mockup.</p>

<div class="section-images">
<img src="${packTesting1}" alt="Pack Testing Example 1" class="section-image" />
<img src="${packTesting2}" alt="Pack Testing Example 2" class="section-image" />
</div>

<h4>3. Pricing Feedback</h4>

<p>We simulate customer reactions to different price points and fallback options. You get insights into:</p>

<ul>
<li>Purchase intent at various prices</li>
<li>Their reasoning</li>
<li>Willingness to pay a different amount if needed</li>
</ul>

<p>💰 <strong>Insight:</strong> The pricing team identified a sweet spot that boosted projected intent by 27%.</p>

<div class="section-images">
<img src="${pricingFeedback1}" alt="Pricing Feedback Example 1" class="section-image" />
<img src="${pricingFeedback2}" alt="Pricing Feedback Example 2" class="section-image" />
</div>

<hr class="section-divider">

<h3>Ready for Action: Clean, Compatible, Customizable</h3>

<p>All results are delivered in structured <strong>JSON</strong> or <strong>Excel-friendly tables</strong>, ready for dashboards, modeling tools, or slide decks. You can filter by persona, test new "what-if" scenarios, and integrate the data with your broader analytics stack - no cleanup required.</p>

<p>What's more, our solution can directly <strong>connect to your internal databases</strong> - such as CRM systems, purchase logs, or previous research reports - to add hyper-contextual data into each simulation. And with <strong>live web scraping</strong>, we keep the results grounded in what's happening in the market right now - not just last quarter.</p>

<hr class="section-divider">

<h3>The Results: Fast, Affordable, Insightful</h3>

<p>Here's a side-by-side look at how our suite compares to traditional research methods:</p>

<div class="comparison-table">
<table>
<tr>
<th>Metric</th>
<th>Old Surveys</th>
<th>ContextJet AI</th>
</tr>
<tr>
<td>Time to Insights</td>
<td>2–4 weeks</td>
<td>Same day</td>
</tr>
<tr>
<td>Cost per Study</td>
<td>Rs. 3–5 lakhs</td>
<td>Fraction of that</td>
</tr>
<tr>
<td>Segments Tested</td>
<td>2–3</td>
<td>10+</td>
</tr>
<tr>
<td>Price Sensitivity</td>
<td>Often skipped</td>
<td>Fully supported</td>
</tr>
<tr>
<td>Data Quality</td>
<td>Varies</td>
<td>Consistent and rich</td>
</tr>
</table>
</div>

<hr class="section-divider">

<h3>Why It Works: These Aren't Just Random AI Responses</h3>

<p>A lot of people hear "synthetic data" and think it must be made up or inaccurate. But that's not how we do things.</p>

<p>Our synthetic responses are:</p>

<ul>
<li>Grounded in real-world language and web trends</li>
<li>Tuned using public consumer behavior patterns</li>
<li>Crafted with prompts that reflect actual decision-making logic</li>
<li>Adjusted by personas with realistic attitudes and opinions</li>
</ul>

<p>That means what you get isn't just "fake data" - it's <strong>insight that feels real, because it's modeled on reality</strong>.</p>

<hr class="section-divider">

<h3>A Smarter Way to Test, Learn, and Launch</h3>

<p>With our Synthetic Market Survey Suite, our client gained a research tool that moves at the speed of business. They now test ideas weekly instead of quarterly, tweak pricing before going to market, and understand what different types of customers are likely to think - without hiring a single focus group.</p>

<p>Here's what this unlocks:</p>

<p>✅ Launch and iterate faster<br>
✅ Cut research costs dramatically<br>
✅ Target niche audiences instantly<br>
✅ Reduce risk on product and pricing decisions</p>

<hr class="section-divider">

<h3>Final Thoughts: Let the AI Do the Heavy Lifting</h3>

<p>At <strong>ContextJet AI</strong>, we're helping companies turn generative AI into a <strong>real-time insight engine</strong>. Our Synthetic Market Survey Suite is more than just a tool - it's a smarter, faster way to build confidence in your decisions.</p>

<p>Let us handle the data.<br>
<strong>You focus on what comes next.</strong></p>`
    },
    {
      categories: ['All'],
      title: 'Nexo: GraphRAG Knowledge Engine',
      description: 'Unlocking enterprise knowledge with multimodal AI-powered graph reasoning that converts scattered data into smart, searchable knowledge bases with intelligent chatbot interfaces.',
      icon: '🕸️',
      image: graphragCover,
      images: [graphrag1, graphrag2, graphrag3],
      metrics: [
        { value: 'Multi-format', label: 'Data Support' },
        { value: 'Real-time', label: 'Query Speed' },
        { value: '360°', label: 'Knowledge View' }
      ],
      technologies: ['GraphRAG', 'Knowledge Graphs', 'Multimodal AI', 'Agentic Workflows'],
      slug: 'nexo-graphrag-engine',
      content: `<h2>Unlocking Knowledge with Multimodal AI-Powered Graph Reasoning</h2>

<p><strong>How ContextJet AI Converts Scattered Enterprise Data into a Smart, Searchable Graph Knowledge Base</strong></p>

<hr class="section-divider">

<h3>Overview</h3>

<p>In today's enterprises, knowledge is everywhere - but it's often trapped in PDFs, spreadsheets, scattered web portals, image snapshots, and emails. The challenge isn't access; it's structure.</p>

<p>That's where our solution comes in. At <strong>ContextJet AI</strong>, we built a powerful system to <strong>turn unstructured and semi-structured data into intelligent, query-ready knowledge graphs</strong> - complete with a friendly chatbot interface and robust retrieval strategies.</p>

<p>Meet a fictional but visually demonstrated assistant called <strong>Nexo</strong> - our intelligent Graph Reasoning Chatbot. While Nexo is a demo interface, the system behind it has been developed and deployed for clients needing deep search, insight synthesis, and AI-driven document reasoning across multiple data types.</p>

<hr class="section-divider">

<h3>The Problem: Knowledge Everywhere, Answers Nowhere</h3>

<p>Organizations today are drowning in data but starving for answers. Teams face a common set of problems:</p>

<ul>
<li>📄 Information is scattered across formats - PDFs, Word files, HTML exports, Excel sheets, markdown notes, plain text, XML, and JSON.</li>
<li>🖼️ Visuals are locked inside PNG, JPEG, and TIFF images with no direct searchability.</li>
<li>📊 Tables in Excel and CSVs hold key metrics - but aren't linked to narrative documents.</li>
<li>🖥️ Structured data in internal databases lives in silos - CRM, ERP, analytics dashboards.</li>
<li>🤷 Even when it's all "accessible," it's not <strong>connected</strong>, queryable, or understood in context.</li>
</ul>

<p>Our client needed more than a search engine - they needed a <strong>reasoning layer</strong> over their documents and datasets.</p>

<hr class="section-divider">

<h3>The Solution: An End-to-End Graph-Based Intelligence Engine</h3>

<p>We built a comprehensive system that ingests multimodal data, understands it, and turns it into a navigable knowledge graph that users can interact with - <strong>via a chat interface</strong> or APIs.</p>

<h4>📚 What It Understands:</h4>

<ul>
<li>Unstructured formats: <strong>PDF, DOCX, TXT, HTML, XML, Markdown, JSON</strong></li>
<li>Image formats: <strong>PNG, JPEG, TIFF</strong></li>
<li>Table formats: <strong>XLSX, CSV</strong></li>
<li>Structured data from: <strong>MySQL, PostgreSQL, MongoDB, Airtable</strong>, and more</li>
</ul>

<p>This knowledge is transformed into a <strong>custom graph</strong> - where paragraphs, tables, images, and entities are linked via meaningful relationships. You can see this in our visual demos, where chunks, concepts, and topics form dynamic and searchable connections.</p>

<img src="MULTIMODAL_GRAPH_PLACEHOLDER" alt="Multimodal Knowledge Graph - Capturing images, tables and text from unstructured documents" class="section-image" />

<p>To support this, we've also implemented internal <strong>density-based clustering pipelines</strong> during the document ingestion phase. These pipelines help <strong>detect hidden patterns, themes, and structural groupings</strong> in large, unstructured datasets. Whether it's identifying topical clusters in a long report or organizing disjointed emails into coherent knowledge units, these algorithms provide the groundwork for a clean, connected graph.</p>

<hr class="section-divider">

<h3>🔍 Advanced Retrieval with GraphRAG</h3>

<p>Once the knowledge graph is built, we enable <strong>GraphRAG</strong> - Retrieval-Augmented Generation enhanced with graph reasoning. Our system doesn't just fetch - it <strong>thinks</strong>.</p>

<p>We support multiple intelligent retrieval strategies:</p>

<ul>
<li><strong>Entity-based hierarchical retrieval</strong> for speed and scalability</li>
<li><strong>Reranker pipelines</strong> for precision and semantic depth</li>
<li><strong>Parallel retrievals</strong> combining Graph, Vector, and Entity search to reduce false negatives</li>
</ul>

<p>The result? You don't just get documents - you get <strong>exact, contextual answers</strong>, sourced and linked to graph nodes you can explore further.</p>

<hr class="section-divider">

<h3>🤖 Agentic Workflows: Smarter Actions, Deeper Reasoning</h3>

<p>To make the system even more adaptive and responsive, we also enable <strong>agentic workflows</strong> within our retrieval and reasoning pipeline. These workflows allow the chatbot to:</p>

<ul>
<li>Dynamically plan multi-step retrieval or reasoning strategies</li>
<li>Incorporate <strong>external tools and APIs</strong> based on query context</li>
<li>Navigate complex decision trees using conditional logic</li>
<li>Trigger follow-up data queries or cross-referencing logic automatically</li>
</ul>

<p>This means our GraphRAG chatbot isn't just reactive - it becomes <strong>proactive</strong>, <strong>tool-aware</strong>, and capable of <strong>deeper reasoning</strong> that spans across connected systems, APIs, or knowledge bases. The same agentic design can also orchestrate tasks across our sales assistant, synthetic survey suite, and enterprise data platforms - making the system modular and interoperable.</p>

<hr class="section-divider">

<h3>🧠 Ontologies That Fit You, Not the Other Way Around</h3>

<p>No two businesses structure knowledge the same way. That's why we support:</p>

<ul>
<li>🧠 <strong>Custom ontology definitions</strong> for industries, teams, or departments</li>
<li>🏗️ <strong>Flexible hierarchy models</strong> (flat, tiered, modular, or cross-linked)</li>
<li>📝 The ability to <strong>evolve the graph</strong> over time - adding new nodes, views, and interpretations as your knowledge grows</li>
</ul>

<p>Whether you're in legal, manufacturing, edtech, or enterprise sales - our framework molds to your ecosystem.</p>

<hr class="section-divider">

<h3>💬 Nexo: Your Smart Knowledge Base Explorer</h3>

<p>At the front of this system sits <strong>Nexo</strong>, our demo chatbot (shown above). Users can interact with the knowledge graph as if talking to a knowledgeable colleague.</p>

<p>It can:</p>

<ul>
<li>Explain how different pieces of data connect</li>
<li>Answer deep questions like "Which pricing strategy worked best for enterprise clients in Q4?"</li>
<li>Provide citations, links to images or tables, and even trace reasoning paths</li>
<li>Let you visually browse how concepts relate - via interactive graph visualizations</li>
</ul>

<img src="CONCEPT_STRUCTURE_PLACEHOLDER" alt="Knowledge Graph Concept Structure - Extracting concept level structure from unstructured documents" class="section-image" />

<p>Behind the scenes, Nexo orchestrates GraphRAG queries, context-aware reasoning, and multi-hop traversal logic - all within milliseconds.</p>

<hr class="section-divider">

<h3>🔌 Extensible by Design</h3>

<p>This solution isn't a one-off - it's a <strong>plug-and-play intelligence layer</strong> that integrates smoothly with:</p>

<ul>
<li>🛒 Our <strong>Sales Agent AI</strong> (let your chatbot access documentation mid-sale)</li>
<li>📊 Our <strong>Synthetic Survey Suite</strong> (grounded data mapped into the graph for instant insights)</li>
<li>💻 Client-side systems like internal wikis, LMSs, helpdesks, or business intelligence dashboards</li>
</ul>

<hr class="section-divider">

<h3>🔗 Real Integrations, Real Impact</h3>

<p>We designed the solution to connect seamlessly with:</p>

<ul>
<li><strong>Cloud storage</strong> (Google Drive, OneDrive, Dropbox)</li>
<li><strong>Internal knowledge bases</strong> (Confluence, Notion, SharePoint)</li>
<li><strong>Messaging interfaces</strong> (Slack, Teams, Web chatbots)</li>
</ul>

<p>This isn't just "search" - it's <strong>structured, conversational intelligence</strong>, available wherever your team works.</p>

<hr class="section-divider">

<h3>The Results: From Search to Structured Clarity</h3>

<div class="comparison-table">
<table>
<tr>
<th>Capability</th>
<th>Before</th>
<th>After</th>
</tr>
<tr>
<td>Document Search</td>
<td>Keyword-based</td>
<td>Context-aware + Graph-assisted</td>
</tr>
<tr>
<td>Retrieval Accuracy</td>
<td>Inconsistent</td>
<td>Multi-layered + reranked</td>
</tr>
<tr>
<td>Knowledge Visibility</td>
<td>Hidden in files</td>
<td>Visual, structured, linked</td>
</tr>
<tr>
<td>Team Access to Insights</td>
<td>Manual handover</td>
<td>Chatbot-based reasoning</td>
</tr>
<tr>
<td>Decision Speed</td>
<td>Days or weeks</td>
<td>Seconds or minutes</td>
</tr>
</table>
</div>

<hr class="section-divider">

<h3>Final Thoughts: Your Data, Reimagined as Insight</h3>

<p>At <strong>ContextJet AI</strong>, we help organizations bring structure to their chaos. Whether it's sales data, academic research, operational handbooks, or historical decisions - our knowledge graph solution turns it all into something you can <strong>ask, explore, and act on</strong>.</p>

<p>We don't just make knowledge searchable.<br><strong>We make it make sense.</strong></p>`
    },
    {
      categories: ['E-commerce', 'Manufacturing', 'Media and Entertainment'],
      title: 'TextGrad-Based Optimization Suite',
      description: 'This innovative solution is currently under development and will leverage advanced text optimization techniques for enhanced AI performance.',
      icon: '⚡',
      image: textgradCover,
      images: [],
      metrics: [],
      technologies: [],
      slug: 'textgrad-optimization',
      content: `<h2>TextGrad-Based Optimization Suite</h2>

<p>Coming soon! Solution building still in progress.</p>`
    }
  ];

  const study = caseStudies.find(s => s.slug === slug);

  if (!study) {
    return (
      <DetailSection>
        <div className="container">
          <BackButton onClick={() => navigate('/case-studies')}>
            ← Back to Case Studies
          </BackButton>
          <h1 className="section-title">Case Study Not Found</h1>
          <p style={{ color: '#a0a0a0' }}>The requested case study could not be found.</p>
        </div>
      </DetailSection>
    );
  }

  return (
    <DetailSection>
      <div className="container">
        <BackButton onClick={() => navigate('/case-studies')}>
          ← Back to Case Studies
        </BackButton>
        
        <div style={{ marginBottom: '1rem' }}>
          {study.categories.map((category, index) => (
            <CategoryBadge key={index}>{category}</CategoryBadge>
          ))}
        </div>
        
        <h1 className="section-title">{study.title}</h1>
        <p className="section-description" style={{ margin: '0 0 2rem 0' }}>
          {study.description}
        </p>

        <HeroImage>
          {study.image ? (
            <img src={study.image} alt={study.title} />
          ) : (
            <span className="icon">{study.icon}</span>
          )}
        </HeroImage>

        <ContentContainer>
          <MainContent>
            <div className="content-text" dangerouslySetInnerHTML={{ __html: getContentWithImages(study) }}>
            </div>
            
          </MainContent>
        </ContentContainer>
      </div>
    </DetailSection>
  );
};

export default CaseStudyDetail;